import { useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { stripShopifyGid, toShopifyProductGid } from "@codway-dev/shared";
import {
  CollapsibleSectionCard,
  SectionHeader,
  ProductPickerButton,
  NumericField,
} from "../../../components/shared/EditorShared";
import type {
  QuantityTiersEditorState,
  QuantityTierEditorTier,
  QuantityTiersTargetProduct,
  QuantityTiersPricingAction,
} from "../../../store/quantity-tiers-editor.mapping";

// ─── Product picker helper ────────────────────────────────────────────────────

async function openProductPicker(
  current: QuantityTiersTargetProduct | null,
  onSelect: (p: QuantityTiersTargetProduct) => void
) {
  try {
    const selected = await window.shopify?.resourcePicker({
      type: "product",
      multiple: false,
      selectionIds: current ? [{ id: toShopifyProductGid(current.id) }] : [],
    });
    if (!selected || selected.length === 0) return;
    const p = selected[0];
    onSelect({
      id: stripShopifyGid(p.id),
      title: p.title,
      imageUrl: p.images[0]?.originalSrc,
      price: p.variants[0]?.price ?? "0",
      compareAtPrice: p.variants[0]?.compareAtPrice ?? undefined,
      defaultVariantId: p.variants[0]
        ? stripShopifyGid(p.variants[0].id)
        : undefined,
      variants: p.variants.map((v) => ({
        id: stripShopifyGid(v.id),
        title: v.title ?? "",
        // Shopify variants may have an explicit image; if not, the variant
        // falls back to the product's main image at render time.
        imageUrl: v.image?.originalSrc ?? v.image?.src ?? undefined,
      })),
    });
  } catch {
    window.shopify?.toast?.show("No se pudo abrir el selector de productos.", {
      isError: true,
    });
  }
}

// ─── Section 1: Name + Product ───────────────────────────────────────────────

export function NameAndProductSection({
  name,
  targetProduct,
  onNameChange,
  onProductChange,
  onProductRemove,
}: {
  name: string;
  targetProduct: QuantityTiersEditorState["targetProduct"];
  onNameChange: (v: string) => void;
  onProductChange: (p: QuantityTiersTargetProduct) => void;
  onProductRemove: () => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={1} title="Configura tu oferta" />}
    >
      <s-stack direction="block" gap="base">
        <s-text-field
          label="Nombre de la oferta"
          value={name}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            onNameChange(e.currentTarget.value)
          }
        />

        <s-stack direction="block" gap="small-200">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-icon type="cart" />
            <s-text type="strong">
              Crear ofertas de cantidad para estos productos:
            </s-text>
          </s-stack>

          <ProductPickerButton
            label="Seleccionar productos"
            selected={targetProduct?.title}
            onClick={() => openProductPicker(targetProduct, onProductChange)}
          />

          {targetProduct && (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
              {targetProduct.imageUrl ? (
                <img
                  src={targetProduct.imageUrl}
                  alt={targetProduct.title}
                  className="w-10 h-10 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <s-icon type="image" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {targetProduct.title}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  ID del producto: {targetProduct.id}
                </p>
              </div>
              <button
                onClick={onProductRemove}
                className="text-red-500 hover:text-red-700 flex-shrink-0"
                aria-label="Quitar producto"
              >
                <s-icon type="delete" />
              </button>
            </div>
          )}
        </s-stack>
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Tier inline editor ───────────────────────────────────────────────────────

const PRICING_OPTIONS: { value: QuantityTiersPricingAction; label: string }[] =
  [
    { value: "none", label: "Sin descuento" },
    { value: "fixed_price_per_item", label: "Precio fijo por unidad" },
    { value: "percentage_off", label: "Porcentaje de descuento" },
    { value: "fixed_amount_off", label: "Descuento fijo" },
  ];

function TierInlineEditor({
  tier,
  onChange,
}: {
  tier: QuantityTierEditorTier;
  onChange: <K extends keyof QuantityTierEditorTier>(
    key: K,
    value: QuantityTierEditorTier[K]
  ) => void;
}) {
  return (
    <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200 flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <s-text-field
            label="Título"
            value={tier.title}
            onInput={(e: CallbackEvent<"s-text-field">) =>
              onChange("title", e.currentTarget.value)
            }
          />
          <span className="text-xs text-gray-400">
            {tier.title.length}/185 · Usa {"{product_name}"} para insertar el
            nombre del producto dinámicamente
          </span>
        </div>
        <NumericField
          label="Cantidad"
          value={tier.quantity}
          min={1}
          onChange={(v) => onChange("quantity", v)}
        />
      </div>

      <s-checkbox
        label="Preseleccionar esta oferta"
        checked={tier.isDefault}
        onChange={(e: CallbackEvent<"s-checkbox">) =>
          onChange("isDefault", e.currentTarget.checked)
        }
      />

      <s-stack direction="block" gap="small-200">
        <s-text>Tipo de descuento</s-text>
        <div className="flex bg-gray-100 p-1 rounded-lg flex-wrap gap-1">
          {PRICING_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange("pricingAction", opt.value)}
              className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                tier.pricingAction === opt.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </s-stack>

      {tier.pricingAction === "percentage_off" && (
        <NumericField
          label="Porcentaje (%)"
          value={tier.pricingPercentage}
          min={0}
          max={100}
          onChange={(v) => onChange("pricingPercentage", v)}
        />
      )}

      {(tier.pricingAction === "fixed_price_per_item" ||
        tier.pricingAction === "fixed_amount_off") && (
        <NumericField
          label={
            tier.pricingAction === "fixed_price_per_item"
              ? "Precio por unidad"
              : "Monto de descuento"
          }
          value={tier.pricingAmount}
          min={0}
          onChange={(v) => onChange("pricingAmount", v)}
        />
      )}

      <s-stack direction="block" gap="small-200">
        <s-text>Etiqueta (opcional)</s-text>
        <s-text-field
          label="Texto"
          value={tier.badgeText}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            onChange("badgeText", e.currentTarget.value)
          }
        />
        <div className="grid grid-cols-2 gap-2">
          <s-color-field
            label="Color del texto"
            value={tier.badgeTextColor}
            onInput={(e: CallbackEvent<"s-color-field">) =>
              onChange("badgeTextColor", e.currentTarget.value)
            }
          />
          <s-color-field
            label="Color de fondo"
            value={tier.badgeBackgroundColor}
            onInput={(e: CallbackEvent<"s-color-field">) =>
              onChange("badgeBackgroundColor", e.currentTarget.value)
            }
          />
        </div>
      </s-stack>

      <s-color-field
        label="Color del título"
        value={tier.titleColor}
        onInput={(e: CallbackEvent<"s-color-field">) =>
          onChange("titleColor", e.currentTarget.value)
        }
      />

      <s-color-field
        label="Color del precio"
        value={tier.priceColor}
        onInput={(e: CallbackEvent<"s-color-field">) =>
          onChange("priceColor", e.currentTarget.value)
        }
      />

      <s-text-field
        label="URL de imagen personalizada (opcional)"
        value={tier.customImageUrl}
        placeholder="https://..."
        onInput={(e: CallbackEvent<"s-text-field">) =>
          onChange("customImageUrl", e.currentTarget.value)
        }
      />

      <s-checkbox
        label="Ocultar precio anterior (tachado)"
        checked={tier.hideCompareAtPrice}
        onChange={(e: CallbackEvent<"s-checkbox">) =>
          onChange("hideCompareAtPrice", e.currentTarget.checked)
        }
      />
    </div>
  );
}

// ─── Section 2: Tiers list ────────────────────────────────────────────────────

export function TiersSection({
  tiers,
  onAdd,
  onRemove,
  onUpdate,
}: {
  tiers: QuantityTierEditorTier[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: <K extends keyof QuantityTierEditorTier>(
    index: number,
    key: K,
    value: QuantityTierEditorTier[K]
  ) => void;
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleExpand = (i: number) =>
    setExpandedIndex((prev) => (prev === i ? null : i));

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={2} title="Configurar las cantidades" />}
    >
      <s-stack direction="block" gap="base">
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_80px_80px] bg-gray-50 border-b border-gray-200 px-4 py-2">
            <s-text type="strong">Título</s-text>
            <s-text type="strong">Cantidad</s-text>
            <s-text type="strong" />
          </div>

          {/* Tier rows */}
          {tiers.map((tier, i) => (
            <div
              key={tier._tempId}
              className="border-b border-gray-100 last:border-b-0"
            >
              <div className="grid grid-cols-[1fr_80px_80px] items-center px-4 py-3">
                <span className="text-sm text-gray-900 truncate pr-2">
                  {tier.title}
                </span>
                <span className="text-sm text-gray-700">{tier.quantity}</span>
                <div className="flex items-center gap-1 justify-end">
                  <button
                    onClick={() => toggleExpand(i)}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                    aria-label="Editar tier"
                    aria-expanded={expandedIndex === i}
                  >
                    <s-icon type="edit" />
                  </button>
                  {tiers.length > 1 && (
                    <button
                      onClick={() => {
                        if (expandedIndex === i) setExpandedIndex(null);
                        onRemove(i);
                      }}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Eliminar tier"
                    >
                      <s-icon type="delete" />
                    </button>
                  )}
                </div>
              </div>

              {expandedIndex === i && (
                <div className="px-4 pb-4">
                  <TierInlineEditor
                    tier={tier}
                    onChange={(key, value) => onUpdate(i, key, value)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <s-button variant="tertiary" icon="plus" onClick={onAdd}>
          Añadir oferta
        </s-button>
      </s-stack>
    </CollapsibleSectionCard>
  );
}
